<template>
  <card extra-classes="bg-white px-4 py-5 sm:px-6 overflow-y-auto">
    <div class="flex">
      <h2 id="timeline-title" class="text-lg font-medium text-gray-900">Attachments</h2>
      <LoadingSpinner class="ml-2 mt-1" />
    </div>
    <div v-if="esignatures.length > 0" class="mt-6 flow-root">
      <ul role="list" class="-mb-8">
        <li
          v-for="sig of esignatures"
          :key="sig.id"
          class="pb-4 pl-3 pr-4 py-3 flex items-center justify-between text-sm"
        >
          <div class="w-0 flex-1 flex items-center">
            <vue-feather type="feather" class="flex-shrink-0 h-5 w-5 text-gray-400" />
            <span class="ml-2 flex-1 w-0 truncate">{{ sig.temp_name }}</span>
            <span
              v-if="sig.status == 'completed'"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
            >
              Completed
            </span>
            <span
              v-else-if="sig.status == 'sent'"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"
            >
              Sent
            </span>
            <span
              v-else-if="sig.status == 'opened'"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"
            >
              Opened
            </span>
          </div>
          <div class="ml-4 flex-shrink-0">
            <ButtonMenu :iconOnly="true" :sections="2">
              <template v-if="sig.docs.length > 0" v-slot:topmenu>
                <a
                  target="_blank"
                  v-for="(doc, index) of sig.docs"
                  :key="index"
                  :href="doc.url"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                >
                  <svg
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                    />
                  </svg>
                  View E-signature Document {{ index + 1 }}
                </a>
              </template>
              <template v-slot:middlemenu>
                <a
                  target="_blank"
                  :href="sig.audit_log_url"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                >
                  <svg
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                    />
                  </svg>
                  View E-signature Audit Log
                </a>
              </template>
            </ButtonMenu>
          </div>
        </li>
      </ul>
    </div>
    <div v-if="contact.files" class="mt-6 flow-root">
      <ul role="list" class="-mb-8">
        <li
          v-for="file of contact.files"
          :key="file.name"
          class="pb-4 pl-3 pr-4 py-3 flex items-center justify-between text-sm"
        >
          <div class="w-0 flex-1 flex items-center">
            <vue-feather type="paperclip" class="flex-shrink-0 h-5 w-5 text-gray-400" />
            <span class="ml-2 flex-1 w-0 truncate">{{ file.name }}</span>
          </div>
          <div class="ml-4 flex-shrink-0">
            <ButtonMenu :iconOnly="true" :sections="2">
              <template v-slot:topmenu>
                <a
                  target="_blank"
                  :href="file.url"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                >
                  <svg
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                    />
                  </svg>
                  View
                </a>
              </template>
              <template v-slot:middlemenu>
                <a
                  @click="handleDelete(file.id)"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                >
                  <svg
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                    <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                    <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                  </svg>
                  Delete
                </a>
              </template>
            </ButtonMenu>
          </div>
        </li>
      </ul>
    </div>
    <FileInput limits="any file" :multiple="true" :modelValue="files" accept="*" @update:modelValue="files = $event" />
    <div v-if="files" class="mt-2 flex justify-end">
      <TButton label="Upload" color="indigo" @click="updateContact()">
        <LocalLoadingSpinner />
      </TButton>
    </div>
  </card>
</template>

<script>
import Card from '../Card/index.vue';
import ButtonMenu from '../shared/ButtonMenu.vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';

export default {
  props: ['contact'],
  components: {
    ButtonMenu,
    Card,
    LoadingSpinner,
  },
  data() {
    return {
      files: null,
      esignatures: [],
    };
  },
  mounted() {
    this.getEsignatures();
  },
  methods: {
    handleDelete(id) {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/attachments/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          if (data.status != 'error') {
            self.$emit('reload');
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Attachment deleted',
              alertDescription:
                'Your attachment was successfully deleted from this candidate.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Attachment deletion error',
              alertDescription: data.message,
            });
          }
        });
    },
    updateContact() {
      const self = this;
      let formData = new FormData();
      for (let i = 0; i < self.files.length; i++) {
        formData.append('files[]', self.files[i], self.files[i].name);
      }
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/contacts/${self.contact.id}`, {
        method: 'PUT',
        body: formData,
        headers: {
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          if (data.status != 'error') {
            self.files = null;
            self.$emit('reload');
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Attachments uploaded',
              alertDescription:
                'Your attachments were successfully uploaded to this teammate.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Attachments upload error',
              alertDescription: data.message,
            });
          }
        });
    },
    getEsignatures() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/esignature_submissions?id=${self.contact.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.esignatures = data.records;
        });
    },
  },
};
</script>
